import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { formatDistance } from 'date-fns';
import { IndividualService } from 'src/core/services/individual.service';
import { LoaderService } from 'src/core/services/loader.service';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { Company } from 'src/interfaces/company';
import { Individual, IndividualType } from 'src/interfaces/individual';

interface SelectableIndividual {
  individual: Individual,
  selected: boolean,
}

interface DialogData {
  title: string;
  message: string;
  types: string | null;
  company: Company | null;
}

@Component({
    selector: 'app-individual-search-dialog',
    templateUrl: './individual-search-dialog.component.html',
    styleUrls: ['./individual-search-dialog.component.scss'],
    standalone: false
})
export class IndividualSearchDialogComponent implements OnInit {

  displayedColumns: string[] = [
    'title',
    'name',
    'contact',
    'type',
    'created_at',
    'selected',
  ];

  allIndividualTypes = "";

  individualsDataSource: MatTableDataSource<SelectableIndividual>;
  page: number = 1;
  limit: number = 10;
  totalNumberOfIndividuals: number = 0;
  individualTypes: IndividualType[] = [];
  individualType = new UntypedFormControl('');
  selectedIndividual: Individual | null = null;

  individualNameFilterControl = this._formBuilder.control('');

  initialized: boolean = false;
  isLoading: boolean = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private _individualService: IndividualService,
    private _localStorageService: LocalStorageService,
    private _prescriptionService: PrescriptionService,
    private _formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public _dialogRef: MatDialogRef<IndividualSearchDialogComponent>,
    private _loaderService: LoaderService
  ) {
    const limit = this._localStorageService.get('INDIVIDUALS', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }
  }

  ngOnInit(): void {

    (async () => {
      const companyId = this.data.company ? this.data.company.id : null;
      const individuals: Individual[] = await this._individualService
        .getIndividuals(this.page, this.limit, this.data.types, "", companyId);

      const selectableIndividuals: SelectableIndividual[] = individuals.map(individual => ({ individual, selected: false }));

      this.individualsDataSource = new MatTableDataSource<SelectableIndividual>(selectableIndividuals);

      this.totalNumberOfIndividuals = this._individualService
        .getTotalNumberOfIndividuals();

      // get individual types 
      const data: any = await this._prescriptionService
        .getFormData(['individual_type']).toPromise();

      this.individualTypes = data.individual_type ? data.individual_type : [];

      this.individualsDataSource.sort = this.sort;

      this.initialized = true;
    })();
    this._prescriptionService
      .getFormData(['individual_type'])
      .subscribe(data => {
        this.individualTypes = data.individual_type;
      });


    this.individualNameFilterControl
      .valueChanges
      .subscribe(value => {
        this.page = 1; // start from page 1 again 
        this.listIndividuals(this.page, this.limit, this.data.types, value);
      });


    this._loaderService
      .isLoading
      .subscribe(loading => this.isLoading = loading);

  }

  onPageChange(event: PageEvent) {

    // Get more orders 
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;

    this.listIndividuals(this.page, this.limit, this.data.types, this.individualNameFilterControl.value)
  }

  listIndividuals(
    page: number = 1,
    limit: number = 5,
    types: string | null = null,
    search: string = "") {

    (async () => {
      this.page = 1;
      const companyId = this.data.company ? this.data.company.id : null;
      const newIndividuals: Individual[] = await this._individualService
        .getIndividuals(page, limit, types, search, companyId);

      const selectableIndividuals: SelectableIndividual[] = newIndividuals.map(individual => ({ individual, selected: false }));

      this.individualsDataSource.data = selectableIndividuals;
      this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();
    })();

  }


  getCreationTime(client: Individual) {
    return formatDistance(new Date(client.created_at), new Date(), { addSuffix: true });
  }

  formatTime(time: string | null) {
    if (!time) return '-';
    return formatDistance(new Date(time), new Date(), { addSuffix: true });
  }

  getIndividualType(individual: Individual) {

    if (!individual.individual_type_id) return "Not Applicable";

    const individualType: IndividualType | undefined = this.individualTypes
      .find(type => type.id === individual.individual_type_id);

    return individualType ? individualType.type : '-';
  }

  async selectIndividual(selectableIndividual: SelectableIndividual) {
    if (this.selectedIndividual) {
      // find the previously selected company a deselect it 
      const previousSelectableCompany: SelectableIndividual | undefined = this.individualsDataSource
        .data
        .find(selectableIndividual => {
          if (!this.selectedIndividual) return false;
          return selectableIndividual.individual.id === this.selectedIndividual.id
        })

      if (previousSelectableCompany) previousSelectableCompany.selected = false;
    }


    this.selectedIndividual = selectableIndividual.individual;
    selectableIndividual.selected = true;
    if (this.selectedIndividual) {
      // get more detailed information with the address
      this.selectedIndividual = await this._individualService.getIndividual(this.selectedIndividual.id);
      // Mark individual selected
      selectableIndividual.selected = this.selectedIndividual ? true : false;
    }


  }
}
